import { defaultPlayersAbove } from "daedalus-core";
import { placePuddleSteps, worldWeatherSeed, type Puddle } from "odyssey-shared";
import type { WorldBoot } from "../world-boot.ts";
import { puddleGroundOf } from "../weather/puddle-world.ts";

/**
 * How long the work ahead of a crossing may run in one frame, in milliseconds: well inside a 60 Hz frame beside the game's
 * own work.
 */
const FRAME_BUDGET_MS = 6;

/**
 * How many tiles or candidates the puddle placement takes between two looks at the clock.
 */
const PUDDLE_SLICE = 24;

/**
 * How long a neighbour whose work ahead failed waits before a chunk landing beside the map may start it again, in
 * milliseconds, so one chunk that will not load does not refetch the whole neighbour on every landing.
 */
export const PREP_RETRY_MS = 10_000;

/**
 * Waits for the next animation frame, so the work ahead yields the frame to the game.
 *
 * @returns A promise that settles on the next frame.
 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * The work a seamless crossing does ahead (ADR-147 decision 18), so standing on the map reached is one short step between
 * two frames: once the ground beside the map holds a neighbour's chunks, every chunk of that neighbour is fetched into the
 * world boot, and its puddles (`placePuddleSteps`, the same placement a stage makes) are placed a few milliseconds a frame.
 * The puddles of each map of a version are kept for the page's life, so a crossing back and forth places them once.
 */
export class CrossingPrep {
  private readonly puddles = new Map<string, Puddle[]>();
  private readonly running = new Map<string, Promise<void>>();
  private readonly failedUntil = new Map<string, number>();

  /**
   * @param boot The world boot, which fetches and holds the chunks.
   * @param frame Waits for the next frame; a test hands in its own.
   * @param now The clock, in milliseconds.
   */
  public constructor(
    private readonly boot: Pick<WorldBoot, "prefetch" | "forget" | "loadedVersion" | "world" | "groundOf">,
    private readonly frame: () => Promise<void> = nextFrame,
    private readonly now: () => number = () => performance.now(),
  ) {}

  /**
   * Starts the work ahead for a neighbour, once; a failure is told, leaves the crossing to do the work itself, and holds
   * the neighbour back for `PREP_RETRY_MS`.
   *
   * @param mapId The neighbour's id.
   * @returns A promise that settles when the work is done or has failed.
   */
  public ready(mapId: string): Promise<void> {
    let running = this.running.get(mapId);
    if (running !== undefined) {
      return running;
    }
    if (this.now() < (this.failedUntil.get(mapId) ?? 0)) {
      return Promise.resolve();
    }
    running = this.ahead(mapId).catch((error: unknown) => {
      this.running.delete(mapId);
      this.failedUntil.set(mapId, this.now() + PREP_RETRY_MS);
      console.warn(`the work ahead of a crossing to ${mapId} did not finish and waits ${PREP_RETRY_MS / 1000} s: ${error instanceof Error ? error.message : String(error)}`);
    });
    this.running.set(mapId, running);
    return running;
  }

  /**
   * Forgets the work ahead for a neighbour the ground beside the map let go, with the chunks the boot fetched for it, so
   * coming back starts it again; its puddles are kept, being small and the same each time.
   *
   * @param mapId The neighbour's id.
   */
  public forget(mapId: string): void {
    this.running.delete(mapId);
    this.boot.forget(mapId);
  }

  /**
   * The puddles of a map of a version, when placed.
   *
   * @param version The world version.
   * @param mapId The map's id.
   * @returns The puddles, or null when they are not placed yet.
   */
  public puddlesOf(version: string, mapId: string): Puddle[] | null {
    return this.puddles.get(`${version}:${mapId}`) ?? null;
  }

  /**
   * Keeps the puddles a stage placed itself, so a later crossing to its map does not place them again.
   *
   * @param version The world version.
   * @param mapId The map's id.
   * @param puddles The puddles.
   */
  public keep(version: string, mapId: string, puddles: Puddle[]): void {
    this.puddles.set(`${version}:${mapId}`, puddles);
  }

  /**
   * The work ahead: the neighbour's chunks, then its puddles a slice at a time.
   *
   * @param mapId The neighbour's id.
   */
  private async ahead(mapId: string): Promise<void> {
    if ((await this.boot.prefetch(mapId)) === null) {
      return;
    }
    const version = this.boot.loadedVersion();
    const world = this.boot.world;
    const ground = this.boot.groundOf(mapId);
    if (version === null || world === null || ground === null || this.puddles.has(`${version}:${mapId}`)) {
      return;
    }
    const layers = ground.chunks.values().next().value?.layers ?? [];
    const band = ground.map.playersAbove ?? defaultPlayersAbove(layers);
    const puddleGround = puddleGroundOf({ tileSize: world.manifest.tileSize, chunkSize: world.manifest.chunkSize, width: ground.map.width, height: ground.map.height, chunks: ground.chunks, surfaces: ground.surfaces, collision: ground.baseCells, assets: world.assets, band });
    const steps = placePuddleSteps(puddleGround, worldWeatherSeed(world.manifest.worldId), mapId, PUDDLE_SLICE);
    let began = this.now();
    let next = steps.next();
    while (next.done !== true) {
      if (this.now() - began >= FRAME_BUDGET_MS) {
        await this.frame();
        began = this.now();
      }
      next = steps.next();
    }
    this.puddles.set(`${version}:${mapId}`, next.value);
  }
}
